import { useState } from 'react';

import { Menu, MenuItem } from '@mui/material';

import { DomesticIncidentReportApi } from '@/api/api.domestic-incident-report';
import { DataGrid } from '@/components';
import { useDataGridColumns } from '@/components/DataGrid/hooks/useDataGridCols';
import {
  DataGridColSettings,
  mergeColumnSettings,
} from '@/components/DataGrid/utils';
import { Button } from '@/components/TailwindComponents';
import { LocalStoragePrefix } from '@/constants';
import { IDomesticIncidentReportRecord } from '@/models';
import { DIRTableColumns } from '@/pages/Tools/DomesticIncidentReport/dir.data';
import { DomesticIncidentReportDeleteModal } from '@/pages/Tools/DomesticIncidentReport/DomesticIncidentReportDeleteModal';
import { DomesticIncidentReportModal } from '@/pages/Tools/DomesticIncidentReport/DomesticIncidentReportModal';
import { DomesticIncidentReportPrintModal } from '@/pages/Tools/DomesticIncidentReport/DomesticIncidentReportPrintModal';
import { isRmsCasesViewOnlyUser } from '@/services/service.kase';
import { ConfirmTypes, useToastStore, useUserStore } from '@/store';
import { getLocalStorageItem } from '@/utils';

const transformDIRToTableRow = (
  dir: IDomesticIncidentReportRecord,
  index: number,
) => {
  return {
    id: dir._id,
    position: index + 1,
    dirId: dir.dirId,
    agency: dir.agency?.name || '',
    incidentNumber: dir.kase?.number || dir.incident?.incident || '',
    reportedDate: dir.incident?.reported_date || '',
    occurredDate: dir.incident?.occured_date || '',
    complaint: dir.incident?.complaint || '',
    address: (() => {
      const addr = dir.incident?.address;
      if (!addr || typeof addr === 'string') return addr || '';
      const a = addr as any;
      if (a.normalized) return a.normalized;
      const line = a.addressLabel
        ? `${a.addressLabel}${a.subAddress ? ` ${a.subAddress}` : ''}`
        : '';
      const city = [a.zipName, a.state, a.zipcode].filter(Boolean).join(', ');
      return [line, city].filter(Boolean).join(', ');
    })(),
    isRestricted: dir.isRestricted,
    hasPrint: dir.hasPrint,
    caseRestrictedByUser: dir.caseRestrictedByUser,
    usersWithCaseAccessPopulated: dir.usersWithCaseAccessPopulated,
    _original: dir,
  };
};

export const DomesticIncidentReportTable = () => {
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editDirId, setEditDirId] = useState<string | null>(null);
  const [viewDirId, setViewDirId] = useState<string | null>(null);
  const [printRow, setPrintRow] = useState<any | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [menuState, setMenuState] = useState<{
    anchorEl: HTMLElement | null;
    row: any | null;
  }>({ anchorEl: null, row: null });

  const { user } = useUserStore();
  const { updateToast } = useToastStore();

  const viewOnly = isRmsCasesViewOnlyUser(user);

  const [columnSettings, setColumnSettings] = useState<DataGridColSettings[]>(
    () => {
      const savedSettings = getLocalStorageItem({
        key: String(user?._id),
        prefix: `domesticIncidentReports-${LocalStoragePrefix.TableSetting}`,
        shouldParse: true,
      });

      if (!savedSettings) {
        return DIRTableColumns;
      }

      return mergeColumnSettings(DIRTableColumns, savedSettings);
    },
  );

  const handlePrint = async (
    action: 'REDACTED' | 'UNREDACTED',
    includeHelpSheet: boolean,
  ) => {
    if (!printRow) return;
    const dirId = printRow.id;

    try {
      const response = await DomesticIncidentReportApi.printFromRecord(
        dirId,
        action,
        includeHelpSheet,
      );

      if (response.data) {
        const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
        const pdfUrl = URL.createObjectURL(pdfBlob);
        const newWindow = window.open('', '_blank');
        const iframe = document.createElement('iframe');
        iframe.src = pdfUrl;
        iframe.width = '100%';
        iframe.height = '100%';
        iframe.style.border = 'none';
        iframe.onload = function () {
          setTimeout(function () {
            iframe.focus();
            iframe?.contentWindow?.print();
          }, 1000);
        };
        newWindow?.document?.body.appendChild(iframe);

        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'DIR printed successfully',
        });
        setPrintRow(null);
        setRefreshKey((prev) => prev + 1);
      }
    } catch {
      updateToast({
        open: true,
        message: 'Failed to print domestic incident report',
      });
    }
  };

  const handleOpenMenu = (
    event: React.MouseEvent<HTMLButtonElement>,
    row: any,
  ) => {
    setMenuState({ anchorEl: event.currentTarget, row });
  };

  const handleCloseMenu = () => {
    setMenuState({ anchorEl: null, row: null });
  };

  const columns = useDataGridColumns({
    type: 'domesticIncidentReports',
    columnSettings,
    actionMethods: {
      onOpenMenu: handleOpenMenu,
    },
  });

  const fetchDIRRecords = async (
    page: number,
    pageSize: number,
    filter: string,
    sortParam?: string,
  ) => {
    try {
      const params: any = {
        page,
        limit: pageSize,
      };

      if (filter) {
        params.filter = filter;
      }

      if (sortParam) {
        params.sort = sortParam;
      }

      const response = await DomesticIncidentReportApi.list(params);
      const { docs, totalDocs } = response.data;

      const transformedRows = docs.map((dir, index) =>
        transformDIRToTableRow(dir, (page - 1) * pageSize + index),
      );

      return {
        rows: transformedRows,
        totalCount: totalDocs,
      };
    } catch {
      return {
        rows: [],
        totalCount: 0,
      };
    }
  };

  const handleDeleteConfirm = async (reason: string) => {
    if (selectedRecord?.id && reason) {
      try {
        await DomesticIncidentReportApi.delete(selectedRecord.id, reason);
        updateToast({
          type: ConfirmTypes.SUCCESS,
          open: true,
          message: 'Domestic incident report deleted successfully',
        });
        setRefreshKey((prev) => prev + 1);
      } catch {
        updateToast({
          open: true,
          message: 'Failed to delete domestic incident report',
        });
      }
    }
    setIsDeleteModalOpen(false);
  };

  const handleModalSuccess = () => {
    setRefreshKey((prev) => prev + 1);
  };

  return (
    <>
      <DataGrid
        key={refreshKey}
        name="domesticIncidentReports"
        fetchData={fetchDIRRecords}
        columns={columns}
        columnSettings={columnSettings}
        setColumnSettings={setColumnSettings}
        getRowId={(row) => row?.id || ''}
        cta={
          viewOnly ? null : (
            <Button
              variant="default"
              size="sm"
              className="rounded-md px-[20px]"
              onClick={() => setIsCreateModalOpen(true)}
            >
              Create Domestic Incident Report
            </Button>
          )
        }
      />

      <Menu
        anchorEl={menuState.anchorEl}
        open={Boolean(menuState.anchorEl)}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={() => {
            if (menuState.row) setViewDirId(menuState.row.id);
            handleCloseMenu();
          }}
        >
          View
        </MenuItem>
        {!viewOnly && [
          <MenuItem
            key="edit"
            onClick={() => {
              if (menuState.row) setEditDirId(menuState.row.id);
              handleCloseMenu();
            }}
          >
            Edit
          </MenuItem>,
          <MenuItem
            key="print"
            onClick={() => {
              if (menuState.row) setPrintRow(menuState.row);
              handleCloseMenu();
            }}
          >
            Print
          </MenuItem>,
          <MenuItem
            key="delete"
            onClick={() => {
              if (menuState.row) {
                setSelectedRecord(menuState.row);
                setIsDeleteModalOpen(true);
              }
              handleCloseMenu();
            }}
            sx={{ color: 'error.main' }}
          >
            Delete
          </MenuItem>,
        ]}
      </Menu>

      {/* Create Modal */}
      {isCreateModalOpen && (
        <DomesticIncidentReportModal
          mode="create"
          open={isCreateModalOpen}
          onClose={() => setIsCreateModalOpen(false)}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* Edit Modal */}
      {editDirId && (
        <DomesticIncidentReportModal
          mode="edit"
          dirId={editDirId}
          open={!!editDirId}
          onClose={() => setEditDirId(null)}
          onSuccess={handleModalSuccess}
        />
      )}

      {/* View Modal */}
      {viewDirId && (
        <DomesticIncidentReportModal
          mode="view"
          dirId={viewDirId}
          open={!!viewDirId}
          onClose={() => setViewDirId(null)}
          onSuccess={handleModalSuccess}
          readOnly={viewOnly}
        />
      )}

      {/* Print Modal */}
      <DomesticIncidentReportPrintModal
        open={!!printRow}
        onClose={() => setPrintRow(null)}
        onPrint={handlePrint}
      />

      <DomesticIncidentReportDeleteModal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
      />
    </>
  );
};
